/**
 * Planungsphase im Cockpit — welche Projekte, welche Zeilen, welche Reihenfolge.
 *
 * Die Hub-Kachel ist auf dem Handy ausgeblendet (ProjectModuleGrid, `!isMobile`);
 * die Liste „Planungsphase — Handlungsbedarf" im Aufgaben-Baustein ist deshalb
 * fuer viele der einzige Ort, an dem eine rote Ampel auffaellt. Ohne React, mit
 * Tests; die Ampel selbst kommt aus `fetchPlanungsAmpeln` (S1-c).
 */
import { getProjectRelation } from '../../services/projectAccess';
import { getPlanungsVerantwortliche, matchesRepresentative } from '../../services/projectRepresentatives';
import { istUebungsprojekt } from '../../services/uebung/uebungService';
import { istSifaProjekt } from '../../services/projectType';
import { contractedQuantity, contractedQuantityOfRow } from '../../services/nepPosition';
import { istLeistungVomTyp } from '../../services/serviceTypeMatch';
import type { ServiceItem } from '../../types';
import type { AmpelProjekt } from '../../services/planungAmpelService';
import { AMPEL_RANG, type AmpelAuszug, baustartAusGlobalData, hatHandlungsbedarf, isoTag, weiterFuer } from '../planungsphase/planungKachel';
import { DashboardProject, DashboardUser, projectStatus } from './dashboardModel';

/** Ein Projekt, wie `fetchPlanungsAmpeln` es erwartet — der Typ des Dienstes, hier nur durchgereicht. */
export type { AmpelProjekt };

/**
 * Wer die Planungs-Ampel eines Projekts im Cockpit sieht: die Verantwortlichen
 * Planungsphase (Vertreterliste, `bereich: 'planung'`). Hat das Projekt keine,
 * plant der Hauptverantwortliche selbst — Eigentuemer, `coordinatorId` oder
 * Position 0 der Vertreterliste (`getProjectRelation`, nichts nachgebaut).
 * Reine Ausfuehrungs-Vertreter sehen sie nicht: sie begehen, sie planen nicht.
 */
export const istPlanungZustaendig = (p: DashboardProject, user: DashboardUser | null | undefined): boolean => {
    if (!user) return false;
    const meta = p.data?.metadata;
    if (!meta) return false;
    const planer = getPlanungsVerantwortliche(meta);
    if (planer.length > 0) return planer.some(rep => matchesRepresentative(rep, user));
    const rel = getProjectRelation(p, user);
    return rel.isOwner || rel.isCoordinator || rel.isPrimaryRepresentative;
};

/** Abgeschlossene und ruhende Projekte haben keine laufende Planungsphase mehr. */
const OHNE_PLANUNG: ReadonlySet<string> = new Set(['finished', 'completed', 'on_hold', 'archived']);

/**
 * Projekte, deren Ampel das Cockpit abfragt. Uebungsprojekte fallen in keine
 * Auswertung; SiFa-Betreuungen kennen keine Planungsphase.
 */
export const waehlePlanungProjekte = (
    projekte: ReadonlyArray<DashboardProject>,
    user: DashboardUser | null | undefined,
): DashboardProject[] =>
    projekte.filter(p =>
        !istUebungsprojekt(p)
        && !!p.data?.metadata
        && !istSifaProjekt(p)
        && !OHNE_PLANUNG.has(projectStatus(p))
        && istPlanungZustaendig(p, user));

/**
 * Steht eine Planungsleistung im LV? Erst die DB-Zeilen (`project_services`),
 * sonst das JSON — dieselbe Vorrangregel wie die Aufgabenliste. NEP zaehlt nur
 * mit gebuchter Menge (contractedQuantity). Ein Tochterprojekt bucht auf das LV
 * der Mutter; das kennt die Listenzeile nicht — der Hinweis 1.6 erscheint fuer
 * Toechter deshalb nur auf der Hub-Kachel, die das Mutter-LV liest.
 */
export const lvPlanungAusZeile = (p: DashboardProject): boolean => {
    const zeilen = p.project_services || [];
    if (zeilen.length > 0) {
        return zeilen.some(z => z.assignment === 'planning_general' && contractedQuantityOfRow(z) > 0);
    }
    const json = (p.data?.metadata?.globalData?.services || []) as ServiceItem[];
    return json.some(s => istLeistungVomTyp(s, 'planning_general') && contractedQuantity(s) > 0);
};

/** Baustart wie auf der Kachel: Ausfuehrungsbeginn, sonst Projektstart (JSON, dann Spalte). */
export const ampelEingangFuer = (p: DashboardProject): AmpelProjekt => ({
    projectId: p.id,
    baustartIso: baustartAusGlobalData(p.data?.metadata?.globalData) ?? isoTag(p.start_date),
    lvHatPlanungsleistung: lvPlanungAusZeile(p),
});

export interface PlanungZeile {
    project: DashboardProject;
    id: string;
    nummer: string;
    name: string;
    ampel: AmpelAuszug;
    /** „Weiter: …" des Leitfadens (Stufe L3); leer, wenn es keinen gibt. */
    weiter: string;
}

const KEIN_WEITER: Readonly<Record<string, string>> = {};

/**
 * Die Liste „Handlungsbedarf": nur rot, gelb und fehler; rot zuerst, dann gelb,
 * der technische Fehler dahinter. Innerhalb der Farbe die meisten abgelaufenen
 * Fristen zuerst, dann Nummer und Name — damit die Liste bei jedem Laden
 * gleich steht. `weiter` (aus `fetchPlanungsAmpeln`) entscheidet nicht, wer in
 * der Liste steht — nur, was unter dem Grund steht.
 */
export const baueHandlungsbedarf = (
    projekte: ReadonlyArray<DashboardProject>,
    ampeln: Readonly<Record<string, AmpelAuszug>>,
    weiter: Readonly<Record<string, string>> = KEIN_WEITER,
): PlanungZeile[] => {
    const zeilen: PlanungZeile[] = [];
    projekte.forEach(p => {
        const ampel = ampeln[p.id];
        if (!ampel || !hatHandlungsbedarf(ampel.farbe)) return;
        const m = p.data?.metadata;
        zeilen.push({
            project: p,
            id: p.id,
            nummer: String(m?.projectNumber || p.project_number || ''),
            name: p.name || m?.projectName || '',
            ampel,
            weiter: weiterFuer(weiter, p.id),
        });
    });
    return zeilen.sort((a, b) =>
        AMPEL_RANG[a.ampel.farbe] - AMPEL_RANG[b.ampel.farbe]
        || (b.ampel.abgelaufeneFristen || 0) - (a.ampel.abgelaufeneFristen || 0)
        || a.nummer.localeCompare(b.nummer)
        || a.name.localeCompare(b.name));
};

export interface FarbZaehler { rot: number; gelb: number; fehler: number }

export const zaehleFarben = (zeilen: ReadonlyArray<PlanungZeile>): FarbZaehler => {
    const z: FarbZaehler = { rot: 0, gelb: 0, fehler: 0 };
    zeilen.forEach(({ ampel }) => {
        if (ampel.farbe === 'rot') z.rot++;
        else if (ampel.farbe === 'gelb') z.gelb++;
        else if (ampel.farbe === 'fehler') z.fehler++;
    });
    return z;
};

/** „2 rot · 1 gelb · 1 nicht lesbar" fuer die Kopfzeile; leer ohne Zeilen. */
export const farbenKurz = (z: FarbZaehler): string =>
    [
        z.rot > 0 ? `${z.rot} rot` : null,
        z.gelb > 0 ? `${z.gelb} gelb` : null,
        z.fehler > 0 ? `${z.fehler} nicht lesbar` : null,
    ].filter((t): t is string => !!t).join(' · ');
